import React, { useContext, useState } from 'react';

import type { GetBioacousticsSampleDataQuery } from '@/__generated__/graphql.js';
import { myhiveLocalization, MyHiveLocalizationKey } from '@/localization/my-hive.js';
import { LanguageContext } from '@/providers/LanguageProvider.js';

import { BioacousticsDailyChart, buildChartData, type ChartRow } from './BioacousticsDailyChart.js';

type BioacousticsData = GetBioacousticsSampleDataQuery['getBioacousticsSampleData'];

function PollinatorRangeBar({ value, min, max, mean, color, decimals = 0 }: {
  value: number | null;
  min: number;
  max: number;
  mean?: number;
  color: string;
  decimals?: number;
}): React.JSX.Element | null {
  if (value == null || max <= min) return null;
  const pct = (n: number): number => Math.max(0, Math.min(100, ((n - min) / (max - min)) * 100));
  const fmt = (n: number): string => n.toFixed(decimals);
  return (
    <div className="flex items-center gap-2 text-xs text-gray-400">
      <span className="tabular-nums">{fmt(min)}</span>
      <div className="relative flex-1 h-1.5 rounded-full bg-gray-100">
        <div className="absolute top-0 left-0 h-full rounded-full" style={{ width: `${pct(value)}%`, backgroundColor: color }} />
        {mean != null && (
          <div
            className="absolute top-1/2 w-0.5 h-3"
            style={{ left: `${pct(mean)}%`, transform: 'translateY(-50%)', backgroundColor: color, opacity: 0.4 }}
          />
        )}
        <div
          className="absolute top-1/2 w-3 h-3 rounded-full border-2 border-white shadow-sm"
          style={{ left: `${pct(value)}%`, transform: 'translate(-50%, -50%)', backgroundColor: color }}
        />
      </div>
      <span className="tabular-nums">{fmt(max)}</span>
    </div>
  );
}

interface HeroChartProps {
  data: BioacousticsData | undefined;
  startDate: string;
  endDate: string;
  syncId?: string;
}

export function HeroChart({ data, startDate, endDate, syncId = 'pollinator' }: HeroChartProps): React.JSX.Element {
  const { language } = useContext(LanguageContext);
  const t = myhiveLocalization[language];
  const [showWeather, setShowWeather] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<ChartRow | null>(null);

  const stats = React.useMemo(() => {
    if (!data) return { buzzMin: 0, buzzMax: 0, buzzMean: 0, richnessMin: 0, richnessMax: 0, richnessMean: 0, mostRecentRow: null as ChartRow | null };
    const rows = buildChartData(data).filter(r => r.date >= startDate && r.date <= endDate);
    const buzzValues = rows.map(r => r.meanBuzzPerHour).filter((v): v is number => v != null);
    const richnessValues = rows.map(r => r.richness);
    const mostRecentRow = [...rows].reverse().find(r => r.meanBuzzPerHour != null) ?? rows[rows.length - 1] ?? null;
    const avg = (arr: number[]): number => arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0;
    return {
      buzzMin: buzzValues.length ? Math.min(...buzzValues) : 0,
      buzzMax: buzzValues.length ? Math.max(...buzzValues) : 0,
      buzzMean: avg(buzzValues),
      richnessMin: richnessValues.length ? Math.min(...richnessValues) : 0,
      richnessMax: richnessValues.length ? Math.max(...richnessValues) : 0,
      richnessMean: avg(richnessValues),
      mostRecentRow,
    };
  }, [data, startDate, endDate]);

  const displayRow = hoveredRow ?? stats.mostRecentRow;

  const dateBadge = (
    <div className="flex items-center gap-2 flex-wrap sm:block">
      <p className="text-base font-inter text-gray-900 leading-tight">
        {displayRow
          ? new Date(displayRow.date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
          : '—'}
      </p>
      <span className="sm:mt-1 inline-block text-xs px-2 py-0.5 rounded-full font-medium"
        style={{ backgroundColor: 'rgba(219,80,18,0.12)', color: 'var(--color-brand-warm-orange-500)' }}>
        {hoveredRow ? t[MyHiveLocalizationKey.BioacousticsSelected] : t[MyHiveLocalizationKey.BioacousticsMostRecent]}
      </span>
    </div>
  );

  const activityStats = (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: 'var(--color-brand-warm-orange-500)' }} />
        <span className="text-xs uppercase tracking-widest text-gray-600"><strong>{t[MyHiveLocalizationKey.BioacousticsActivity]}</strong></span>
      </div>
      <div className="flex items-baseline gap-2 sm:block">
        <p className="text-4xl sm:text-6xl font-inter text-gray-900 leading-none">
          {displayRow?.meanBuzzPerHour != null ? displayRow.meanBuzzPerHour.toFixed(1) : '—'}
        </p>
        <p className="text-sm font-medium sm:mt-1" style={{ color: 'var(--color-brand-warm-orange-500)' }}>{t[MyHiveLocalizationKey.BioacousticsDetectionsPerHour]}</p>
      </div>
      <p className="text-xs uppercase tracking-widest text-gray-300 mt-2 mb-1">{t[MyHiveLocalizationKey.BioacousticsRangeOverPeriod]}</p>
      <PollinatorRangeBar
        value={displayRow?.meanBuzzPerHour ?? null}
        min={stats.buzzMin}
        max={stats.buzzMax}
        mean={stats.buzzMean}
        color="var(--color-brand-warm-orange-500)"
        decimals={1}
      />
    </div>
  );

  const varietyStats = (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: 'var(--color-brand-teal-500)' }} />
        <span className="text-xs uppercase tracking-widest text-gray-600"><strong>{t[MyHiveLocalizationKey.BioacousticsVariety]}</strong></span>
      </div>
      <div className="flex items-baseline gap-2 sm:block">
        <p className="text-4xl sm:text-6xl font-inter text-gray-900 leading-none">
          {displayRow ? Math.round(displayRow.richness) : '—'}
        </p>
        <p className="text-sm font-medium sm:mt-1" style={{ color: 'var(--color-brand-teal-500)' }}>{t[MyHiveLocalizationKey.BioacousticsUniqueFrequencies]}</p>
      </div>
      <p className="text-xs uppercase tracking-widest text-gray-300 mt-2 mb-1">{t[MyHiveLocalizationKey.BioacousticsRangeOverPeriod]}</p>
      <PollinatorRangeBar
        value={displayRow ? displayRow.richness : null}
        min={stats.richnessMin}
        max={stats.richnessMax}
        mean={stats.richnessMean}
        color="var(--color-brand-teal-500)"
      />
    </div>
  );

  return (
    <div className="flex flex-col sm:flex-row sm:h-full">
      {/* Charts column */}
      <div className="sm:flex-1 min-w-0 flex flex-col px-2 pt-3 pb-2 gap-0">

        {/* Activity header + weather toggle */}
        <div className="flex items-center justify-between pr-2 mb-1 shrink-0">
          <span className="text-xs uppercase tracking-widest text-gray-600"><strong>{t[MyHiveLocalizationKey.BioacousticsActivity]}</strong></span>
          <button
            className="px-2 py-0.5 rounded text-xs border transition-colors"
            style={{
              borderStyle: 'dashed',
              borderColor: showWeather ? 'var(--color-brand-teal-500)' : 'var(--color-brand-teal-dark-200)',
              color: showWeather ? 'var(--color-brand-teal-500)' : 'var(--color-brand-warm-gray)',
              backgroundColor: showWeather ? 'rgba(0,109,106,0.08)' : 'transparent',
            }}
            onClick={() => setShowWeather(v => !v)}
          >{t[MyHiveLocalizationKey.BioacousticsWeather]}</button>
        </div>

        {/* Activity chart */}
        <div className="h-[140px] sm:flex-[2] sm:h-auto min-h-0">
          <BioacousticsDailyChart
            data={data}
            metric="buzz"
            chartType="line"
            startDate={startDate}
            endDate={endDate}
            showWeather={showWeather}
            showTrend
            onHoverRow={setHoveredRow}
            syncId={syncId}
            showTooltip={false}
          />
        </div>

        {/* Activity numbers — mobile only */}
        <div className="sm:hidden border-t border-gray-100 mt-2 pt-3 pb-1 px-1 flex flex-col gap-3">
          {dateBadge}
          {activityStats}
        </div>

        <div className="border-t border-gray-100 my-2 shrink-0" />

        {/* Variety header */}
        <div className="shrink-0 mb-1">
          <span className="text-xs uppercase tracking-widest text-gray-600"><strong>{t[MyHiveLocalizationKey.BioacousticsVariety]}</strong></span>
        </div>

        {/* Variety chart */}
        <div className="h-[100px] sm:flex-[1] sm:h-auto min-h-0">
          <BioacousticsDailyChart
            data={data}
            metric="richness"
            chartType="bar"
            startDate={startDate}
            endDate={endDate}
            showWeather={false}
            onHoverRow={setHoveredRow}
            syncId={syncId}
            showTooltip={false}
          />
        </div>

        {/* Variety numbers — mobile only */}
        <div className="sm:hidden border-t border-gray-100 mt-2 pt-3 pb-1 px-1">
          {varietyStats}
        </div>

      </div>

      {/* Sidebar — desktop only */}
      <div className="hidden sm:flex sm:w-44 shrink-0 border-l border-gray-100 p-4 flex-col gap-5 justify-start">
        {dateBadge}
        {activityStats}
        <div className="border-t border-gray-100" />
        {varietyStats}
      </div>
    </div>
  );
}
